import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import CustomDatePicker from "../../components/CustomDatePicker";
import Pagination from "../../components/Pagination";
import MyAccountFilter from "../MyAccount/MyAccountFilter";
import { apiGet } from "../../Utils/apiFetch";
import { formatDate, toFixedWithoutRounding } from "../../Utils/Constant";

const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
const createDefaultFilter = () => ({
  page: 1,
  page_size: 10,
  keyword: "",
  fromPeriod: moment().subtract(1, "day").startOf("day").format(DATE_FORMAT),
  toPeriod: moment().endOf("day").format(DATE_FORMAT),
  filterByDay: "",
  limit: 100,
});
const numberValue = (value) => Number(value) || 0;

const SpinReportView = ({ title, endpoint, amountField, amountLabel, fileName }) => {
  const tableRef = useRef(null);
  const [filter, setFilter] = useState(createDefaultFilter);
  const [appliedFilter, setAppliedFilter] = useState(filter);
  const [data, setData] = useState({ data: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const getData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const { status, data: response } = await apiGet(endpoint, appliedFilter);
        if (cancelled) return;
        if (status !== 200 || !response?.success) {
          throw new Error(response?.message || "Unable to load the spin report.");
        }
        setData(response.results || { data: [] });
      } catch (err) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message || err?.message || "Unable to load the spin report.";
        setData({ data: [] });
        setError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    getData();
    return () => {
      cancelled = true;
    };
  }, [endpoint, appliedFilter]);

  const applyFilter = (nextFilter) => {
    const from = moment(nextFilter.fromPeriod);
    const to = moment(nextFilter.toPeriod);
    if (!from.isValid() || !to.isValid() || from.isAfter(to)) {
      toast.error("Please select a valid period with the start before the end.");
      return;
    }
    const next = { ...nextFilter, page: 1 };
    setFilter(next);
    setAppliedFilter(next);
  };

  const applyPreset = (preset) => {
    applyFilter({
      ...filter,
      filterByDay: preset,
      fromPeriod: (preset === "today" ? moment() : moment().subtract(1, "day"))
        .startOf("day")
        .format(DATE_FORMAT),
      toPeriod: moment().endOf("day").format(DATE_FORMAT),
    });
  };

  const handleDateChange = (update) => {
    setFilter((prev) => ({
      ...(typeof update === "function" ? update(prev) : update),
      filterByDay: "",
    }));
  };

  const handleLimitChange = (event) => {
    const limit = event.target.value === "" ? "" : Number(event.target.value);
    setFilter((prev) => ({ ...prev, limit, page: 1 }));
    setAppliedFilter((prev) =>
      prev.limit === limit ? prev : { ...prev, limit, page: 1 },
    );
  };

  const rows = Array.isArray(data.data) ? data.data : [];
  const totals = rows.reduce(
    (sum, row) => ({
      spin: sum.spin + numberValue(row.spin),
      // Sum the displayed amounts in cents to avoid floating point drift.
      amountCents: sum.amountCents + Math.round(
        Number(toFixedWithoutRounding(row[amountField] || 0)) * 100,
      ),
    }),
    { spin: 0, amountCents: 0 },
  );
  const page = Number(data.page) || appliedFilter.page;
  const totalPages = Number(data.totalPages) || 0;
  const pagination = {
    ...data,
    page,
    totalPages,
    hasPrevPage: page > 1,
    hasNextPage: page < totalPages,
  };

  return (
    <app-global-report _nghost-serverapp-c117="" className="ng-star-inserted">
      <div _ngcontent-serverapp-c117="" className="main_wrap">
        <div _ngcontent-serverapp-c117="" className="box">
          <h2>{title}</h2>
        </div>
        <form onSubmit={(event) => {
          event.preventDefault();
          applyFilter(filter);
        }}>
          <div className="function-wrap">
            <ul className="input-list flowroot">
              <li><label>Period</label></li>
              <CustomDatePicker filter={filter} setFilter={handleDateChange} />
              <li><label htmlFor="spin-username">Username</label></li>
              <li>
                <input
                  id="spin-username"
                  type="text"
                  placeholder="Search by UserName"
                  value={filter.keyword}
                  onChange={(event) => setFilter((prev) => ({
                    ...prev,
                    keyword: event.target.value,
                  }))}
                />
              </li>
              <li><label htmlFor="spin-limit">Last:</label></li>
              <li>
                <select
                  id="spin-limit"
                  value={filter.limit}
                  onChange={handleLimitChange}
                >
                  {[100, 200, 500, 1000, 5000, 10000].map((limit) => (
                    <option key={limit} value={limit}>{limit} Txn</option>
                  ))}
                  <option value="">All</option>
                </select>
              </li>
            </ul>
            <ul className="input-list flowroot">
              <li>
                <button
                  type="button"
                  className="btn"
                  disabled={isLoading}
                  aria-pressed={filter.filterByDay === "today"}
                  onClick={() => applyPreset("today")}
                >
                  Just for today
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="btn"
                  disabled={isLoading}
                  aria-pressed={filter.filterByDay === "yesterday"}
                  onClick={() => applyPreset("yesterday")}
                >
                  From yesterday
                </button>
              </li>
              <li>
                <button type="submit" className="btn-send" disabled={isLoading}>Search</button>
              </li>
              <li>
                <button
                  type="button"
                  className="btn"
                  disabled={isLoading}
                  onClick={() => applyFilter(createDefaultFilter())}
                >
                  Reset
                </button>
              </li>
            </ul>
          </div>
        </form>
        <div id="noReportMessage">{error && <p role="alert">{error}</p>}</div>
        <div className="total_all">
          <span className="total-users" style={{ fontWeight: 600 }}>
            Total Records: {data.totalDocs ?? data.count ?? rows.length}
          </span>
          <span className="spacer" />
          <MyAccountFilter type="new" tableRef={tableRef} fileName={fileName} />
        </div>
        <div id="reportDiv" className="over-wrap" aria-busy={isLoading}>
          <div className="table-responsive data-table">
            <table ref={tableRef} className="table01 table-pt">
              <thead>
                <tr>
                  <th scope="col" className="align-L">Username</th>
                  <th scope="col">Spin Count</th>
                  <th scope="col">{amountLabel}</th>
                  <th scope="col" className="align-L">Created Date</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={4} className="align-L">Loading...</td></tr>
                ) : rows.length ? rows.map((row, index) => (
                  <tr key={row._id || index}>
                    <td className="align-L">{row.username || "-"}</td>
                    <td>{numberValue(row.spin)}</td>
                    <td>{toFixedWithoutRounding(row[amountField] || 0)}</td>
                    <td className="align-L">{row.createdAt ? formatDate(row.createdAt) : "-"}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={4} className="align-L">{error ? "Unable to load records." : "No records found"}</td></tr>
                )}
              </tbody>
              {!isLoading && rows.length > 0 && (
                <tfoot>
                  <tr className="total">
                    <td className="align-L">Total</td>
                    <td>{totals.spin}</td>
                    <td>{toFixedWithoutRounding(totals.amountCents / 100)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        {!isLoading && totalPages > 1 && (
          <Pagination pagination={pagination} onPageChange={(nextPage) => {
            setAppliedFilter((prev) => ({ ...prev, page: nextPage }));
          }} />
        )}
      </div>
    </app-global-report>
  );
};

export default SpinReportView;
